/* eslint-disable react/no-unused-state */
/* eslint-disable react/no-did-update-set-state */
import React from 'react';
import { withTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { ChevronRight } from '@saleshandy/icons';
import { RequestStatus } from '../../../../shared/enums/request-status';
import {
  executeOnRequestStatusWithPrevStatusCheck,
  redirectWithRefresh,
} from '../../../../shared/utils';
import {
  SequenceAction,
  SequenceProgress,
  SequenceSortBy,
  SequenceTab,
} from '../../enums';
import SequenceSingleTitle from './components/sequence-single-title';
import { IProps } from './types';
import { planError } from '../../../../shared/utils/errors/plan-permission-error/plan-error';
import { SubscriptionPlans } from '../../../../shared/utils/subscription-plans';
import { UserRole } from '../../../../shared/enums/user-roles';
import hasResource from '../../../../shared/utils/access-control/has-resource';
import { ResourceIdentifiers } from '../../../../shared/utils/access-control/enums/resource-identifiers';
import hasPermission from '../../../../shared/utils/access-control/has-permission';
import Switch, {
  Size,
} from '../../../../shared/design-system/components/atoms/switch';
import { ProductTour } from '../../../../shared/enums/product-tour';
import { Permissions } from '../../../../shared/utils/access-control/enums/permissions';
import { getLabel } from '../sequence-single-content/components/sequence-single-contacts/components/contact-list-content/components/prospect-activity-modal/helpers';
import { Placement } from '../../../../shared/design-system/components/overlay';
import {
  getSequenceAction,
  getTooltipText,
} from '../sequence-single-content/components/sequence-single-header-actions/utils/helper';
import { sequencePauseMessages } from '../../enums/sequence-pause-reasons';
import UpgradePlanErrorModal from '../../../../shared/components/upgrade-plan-error-modal';
import VerifyEmailModal from '../../../../shared/components/verify-email-modal';
import PauseResumeSequenceModal from '../../shared/modals/pause-resume-sequence-modal';
import RestrictionErrorModal from '../../../prospect/components/prospect-list/components/modals/restriction-error-modal';
import {
  getEmailSendingRestrictionModalContent,
  getOverLimitConnectedEmailErrorMsg,
} from '../../../prospect/components/prospect-list/utils/helper';
import { getEmailBody } from '../../../home/components/header/utils/helper';
import planPermissionError from '../../../../shared/utils/errors/plan-permission-error';
import { isFreePlan } from '../../../../shared/utils/plans';
import { Order } from '../../../../shared/enums/order';
import NoResultGradientImage from '../../../../shared/components/images/no-result-gradient';

type IState = {
  showPauseResumeSequenceModal: boolean;
  action: SequenceAction;
  showRestrictionModal: boolean;
  showUpgradePlanErrorModal: boolean;
  isVerifyEmailModalShow: boolean;
  isRestrictionModalVisible: boolean;
  shouldPauseSubSequencesRef: boolean;
};

class SequenceSingleHeader extends React.Component<IProps, IState> {
  switchRef: React.RefObject<any>;

  constructor(props) {
    super(props);
    this.state = {
      showPauseResumeSequenceModal: false,
      action: null,
      showRestrictionModal: false,
      showUpgradePlanErrorModal: false,
      isVerifyEmailModalShow: false,
      isRestrictionModalVisible: false,
      shouldPauseSubSequencesRef: false,
    };
    this.showPauseResumeSequenceModal = this.showPauseResumeSequenceModal.bind(
      this,
    );
    this.hidePauseResumeSequenceModal = this.hidePauseResumeSequenceModal.bind(
      this,
    );
    this.onSwitchChange = this.onSwitchChange.bind(this);
    this.onPauseResumeSequenceModalSubmit = this.onPauseResumeSequenceModalSubmit.bind(
      this,
    );
    this.onSequenceTitleChange = this.onSequenceTitleChange.bind(this);
    this.switchRef = React.createRef();
  }

  componentDidUpdate(prevProps: Readonly<IProps>) {
    const {
      pauseResumeSequenceRequestStatus,
      connectEmailAccountRequestStatus,
      authUrl,
      currentTab,
      sequenceId,
      isPauseResumeSequence,
      sendGetSubsequencesRequest,
    } = this.props;
    const { action, shouldPauseSubSequencesRef } = this.state;

    if (isPauseResumeSequence) {
      executeOnRequestStatusWithPrevStatusCheck({
        status: pauseResumeSequenceRequestStatus,
        prevStatus: prevProps.pauseResumeSequenceRequestStatus,
        onSuccess: () => {
          this.hidePauseResumeSequenceModal();
          this.setState({ shouldPauseSubSequencesRef: false });

          // Defer API calls to allow React to finish current render
          setTimeout(() => {
            if (
              currentTab === SequenceTab.Subsequence &&
              action === SequenceAction.Pause &&
              shouldPauseSubSequencesRef
            ) {
              sendGetSubsequencesRequest({
                sequenceId,
                page: 1,
                limit: 25,
                order: Order.Desc,
                sortBy: SequenceSortBy.CreatedAt,
                clients: '',
                owners: '',
                search: '',
              });
            }
          }, 0);
        },
        onFailed: () => {
          this.hidePauseResumeSequenceModal();
        },
      });
    }

    if (
      connectEmailAccountRequestStatus !==
        prevProps.connectEmailAccountRequestStatus &&
      connectEmailAccountRequestStatus === RequestStatus.Succeeded
    ) {
      redirectWithRefresh(authUrl);
    }
  }

  onSequenceTitleChange(title: string) {
    const { sequenceId, sendRenameSequenceRequest } = this.props;
    sendRenameSequenceRequest(sequenceId, title);
  }

  onSwitchChange(checked: boolean) {
    const { subscriptionPlan, role } = this.props;
    if (subscriptionPlan === SubscriptionPlans.FREE) {
      if (role === UserRole.ADMIN) {
        planError(2003);
      } else {
        planError(3003);
      }
    } else {
      const action = checked ? SequenceAction.Resume : SequenceAction.Pause;
      this.showPauseResumeSequenceModal(action);
    }
  }

  onPauseResumeSequenceModalSubmit(shouldPauseSubSequences = false) {
    const { action } = this.state;
    const { sequenceId, sendPauseResumeSequenceRequest } = this.props;
    if (!hasPermission(Permissions.SEQUENCE_UPDATE_STATUS) && isFreePlan()) {
      const title = 'Please upgrade to enable the sequence';
      const content = [
        'Your account is downgraded to a free plan. Upgrade to a paid plan to enable the sequence and resume email sending.',
      ];

      planPermissionError.showModal(title, content, true);
      return;
    }

    if (hasResource(ResourceIdentifiers.SEQUENCES_PAUSE_RESUME)) {
      this.setState({ shouldPauseSubSequencesRef: shouldPauseSubSequences });
      sendPauseResumeSequenceRequest(
        sequenceId,
        action,
        shouldPauseSubSequences,
      );
    }
  }

  hideRestrictionModal = () => {
    this.setState({ isRestrictionModalVisible: false });
  };

  onToggleSequenceAction = (checked: boolean) => {
    const {
      remainingEmailSentLimit,
      sequence,
      subscriptionDetails,
      verified,
    } = this.props;

    if (isFreePlan()) {
      const title = 'Please upgrade to enable the sequence';
      const content = [
        'Your account is downgraded to a free plan. Upgrade to a paid plan to enable the sequence and resume email sending.',
      ];

      planPermissionError.showModal(title, content, true);
      return;
    }

    if (remainingEmailSentLimit === 0 && checked) {
      this.showRestrictionModal();
      return;
    }

    if (sequence?.isSequenceHasOverLimitEmails) {
      this.showRestrictionModal();
      return;
    }

    if (subscriptionDetails?.planCode === SubscriptionPlans.FREE) {
      planError(2003);
    } else if (this.shouldShowPlanUpgradeErrorModal(subscriptionDetails)) {
      this.setState({ showUpgradePlanErrorModal: true });
      // setShowUpgradePlanErrorModal(true);
    } else if (!verified) {
      this.showVerifyEmailModal();
    } else {
      const action = getSequenceAction(checked);
      hasResource(ResourceIdentifiers.SEQUENCES_PAUSE_RESUME) &&
        this.showPauseResumeSequenceModal(action);
    }
  };

  shouldShowPlanUpgradeErrorModal = (subscriptionDetails) =>
    subscriptionDetails?.planCode === SubscriptionPlans.EMAIL_STARTER_MONTHLY ||
    subscriptionDetails?.planCode === SubscriptionPlans.EMAIL_STARTER_YEARLY;

  showRestrictionModal = () => {
    this.setState({ showRestrictionModal: true });
  };

  showVerifyEmailModal = () => {
    this.setState({ isVerifyEmailModalShow: true });
  };

  hideVerifyEmailModal = () => {
    this.setState({ isVerifyEmailModalShow: false });
  };

  hideUpgradePlanErrorModal = () => {
    this.setState({ showUpgradePlanErrorModal: false });
  };

  showPauseResumeSequenceModal(action: SequenceAction) {
    const { setIsPauseResumeSequence } = this.props;
    this.setState({ action, showPauseResumeSequenceModal: true });
    setIsPauseResumeSequence(true);
  }

  hidePauseResumeSequenceModal() {
    this.setState({ showPauseResumeSequenceModal: false });
  }

  render() {
    const {
      title,
      sequenceId,
      progress,
      pausedReasonIdentifier,
      pauseResumeSequenceRequestStatus,
      remainingEmailSentLimit,
      t,
      match: { params },
      sequenceStatsCounts,
      sequenceCreationSource,
    } = this.props;
    const {
      showPauseResumeSequenceModal,
      showUpgradePlanErrorModal,
      isRestrictionModalVisible,
      isVerifyEmailModalShow,
      action,
    } = this.state;

    const isSequenceActive = progress === SequenceProgress.Active;
    const reason = pausedReasonIdentifier
      ? sequencePauseMessages[pausedReasonIdentifier]
      : '';
    const isPauseResumeSequenceRequestPending =
      pauseResumeSequenceRequestStatus === RequestStatus.Pending;

    const isSubsequenceDetailsPage = params?.subsequenceId;
    const fromParamSequenceId = params?.sequenceId;

    const sequenceSingleHeaderClassName = classNames([
      'single-sequence-header d-flex align-items-center',
      {
        'subsequence-details-header': isSubsequenceDetailsPage,
      },
    ]);

    return (
      <>
        <div className="d-flex justify-content-between align-items-center">
          <div className={sequenceSingleHeaderClassName}>
            <Link className="single-sequence-header--title" to="/sequence">
              Sequence
            </Link>
            <div className="single-sequence-header--icon">
              <ChevronRight />
            </div>
            {isSubsequenceDetailsPage ? (
              <Link
                className="single-sequence-header--title"
                to={`/sequence/${fromParamSequenceId}/steps`}
              >
                <SequenceSingleTitle
                  title={title}
                  onChange={this.onSequenceTitleChange}
                  sequenceId={sequenceId}
                  isSubsequence={true}
                  sequenceCreationSource={sequenceCreationSource || ''}
                />
              </Link>
            ) : (
              <SequenceSingleTitle
                title={title}
                onChange={this.onSequenceTitleChange}
                sequenceId={sequenceId}
                isSubsequence={false}
                sequenceCreationSource={sequenceCreationSource || ''}
              />
            )}
            {sequenceCreationSource === 'ai_generated' && (
              <div className="single-sequence-header--icon-divider">
                <NoResultGradientImage width={20} height={20} />
                <div className="divider" />
              </div>
            )}

            {hasPermission(Permissions.SEQUENCE_WRITE) &&
              !isSubsequenceDetailsPage && (
                <div id={ProductTour.ActiveSequence} className="switch-mt">
                  <Switch
                    checked={isSequenceActive}
                    label={getLabel(progress)}
                    size={Size.Small}
                    onChange={this.onToggleSequenceAction}
                    ref={this.switchRef}
                    placement={Placement.Bottom}
                    tooltip={getTooltipText(reason, t)}
                    overlayTooltipClass="sequence-switch-tooltip"
                  />
                </div>
              )}
          </div>
        </div>

        <PauseResumeSequenceModal
          show={showPauseResumeSequenceModal}
          action={action}
          onSubmit={this.onPauseResumeSequenceModalSubmit}
          onClose={this.hidePauseResumeSequenceModal}
          isRequestPending={isPauseResumeSequenceRequestPending}
          shouldShowPauseSubsequenceCheckbox={
            sequenceStatsCounts?.subSequenceCount > 0
          }
          subsequenceCount={sequenceStatsCounts?.subSequenceCount}
        />

        <UpgradePlanErrorModal
          show={showUpgradePlanErrorModal}
          onClose={this.hideUpgradePlanErrorModal}
          planName="starter"
          restrictionOn="sequence"
        />

        {isRestrictionModalVisible && (
          <RestrictionErrorModal
            show={isRestrictionModalVisible}
            modalTitle="Upgrade your plan to resume sequence"
            bodyContent={
              remainingEmailSentLimit === 0
                ? getEmailSendingRestrictionModalContent()
                : getOverLimitConnectedEmailErrorMsg()
            }
            emailBody={getEmailBody()}
            onClose={this.hideRestrictionModal}
          />
        )}

        <VerifyEmailModal
          show={isVerifyEmailModalShow}
          onClose={this.hideVerifyEmailModal}
        />
      </>
    );
  }
}

export default withTranslation()(SequenceSingleHeader);
